'use client'

// frontend/src/hooks/useRampCases.ts
//
// The reads and writes behind a live Supervity Auto ramp case on the HR Case
// Management page. Contract: `docs/wiring/01-the-contract.md`.
//
// Hand-rolled `useState` / `useCallback` / `useEffect`, the same shape as
// `useAutoRuns`, because this app has no SWR and no react-query and is not
// getting one for a ramp (correction C15).
//
// The GET endpoints return `HRCase` objects ALREADY MAPPED by the backend, so
// there is no translation layer here and there must not become one: the mapping
// lives once, in Python, where it is testable. What this file does do is coerce
// the four list-valued fields to arrays before a component indexes into them, so
// a backend that omits `policyTriggers` on a case with no rules costs a blank
// section rather than a white screen in front of a room.

import { DEMO_FULL_NAME } from '@/lib/demo-identity'
import { useState, useEffect, useCallback } from 'react'
import { apiClient } from '@/lib/api-client'
import { warnIfTruncated, type PaginationMeta } from '@/lib/pagination'
import type { HRCase } from '@/components/hr-case-management/types/case.types'
import type { LaunchPlan, PlanCohort } from '@/components/hr-case-management/components/LaunchPlanVisual'
import type { CaseException } from '@/components/hr-case-management/components/ExceptionVisual'

/**
 * How many ramp cases the queue asks for.
 *
 * Five was the contract's example and it silently truncated: the list is sorted
 * newest first, so a few live runs pushed the seeded cases — the ones a demo
 * falls back on — off the end of the queue without anything saying so. Twelve
 * covers every ramp a session realistically opens; the backend's ceiling is 50.
 */
export const RAMP_CASES_PAGE_SIZE = 12

/** Every live ramp case carries `id = "ramp-" + ramp_id`. Contract, field mapping. */
export const RAMP_CASE_PREFIX = 'ramp-'

/**
 * Whoever is sitting in front of the screen, for `decided_by` and `sent_by`.
 *
 * A single constant rather than a session lookup: the decision endpoint records
 * this string in the governance trail, and one honest role beat a display name
 * that the demo login may not carry.
 *
 * REVISED 21 Sep. The login does carry a name now, pinned in `demo-identity`,
 * and the first run on the migrated Auto account recorded "HR Service Delivery
 * Specialist" against every decision while Ciby was the person taking them.
 * "Wherever it is super admin, it has to be mine." The role is still the
 * fallback, which is what an unpinned deployment gets.
 */
export const RAMP_OPERATOR = DEMO_FULL_NAME || 'HR Service Delivery Specialist'

/** Is this case a live ramp, or one of the sixteen authored mocks. */
export function isRampCase(id: string | undefined | null): boolean {
  return typeof id === 'string' && id.startsWith(RAMP_CASE_PREFIX)
}

interface RampCaseListResponse {
  items?: HRCase[]
  pagination?: PaginationMeta
}

/**
 * Guarantee the list-valued fields exist.
 *
 * NOT a mapping layer. Every value below is passed through untouched; only a
 * missing list becomes an empty one.
 */
function coerceRampCase(raw: HRCase): HRCase {
  return {
    ...raw,
    policyTriggers: Array.isArray(raw?.policyTriggers) ? raw.policyTriggers : [],
    agentActions: Array.isArray(raw?.agentActions) ? raw.agentActions : [],
    chatHistory: Array.isArray(raw?.chatHistory) ? raw.chatHistory : [],
    relatedSystems: Array.isArray(raw?.relatedSystems) ? raw.relatedSystems : [],
  }
}

/**
 * Fetch the live ramp cases, already in `HRCase` shape.
 *
 * Exported as a plain function as well as through the hook because `caseStore`
 * needs the same rows from inside a context provider's effect, and a second copy
 * of the endpoint string is exactly how the two would drift apart.
 */
export async function fetchRampCases(limit = RAMP_CASES_PAGE_SIZE): Promise<HRCase[]> {
  const result = await apiClient.get<RampCaseListResponse | HRCase[]>(
    `/api/auto/ramp-cases?limit=${limit}`
  )

  // The house shape is `{ items, pagination }`, but a bare array is cheap to
  // tolerate and saves the queue from showing nothing over a spelling.
  const items = Array.isArray(result) ? result : (result?.items ?? [])
  if (!Array.isArray(result)) warnIfTruncated('useRampCases', result?.pagination)

  return items.filter((item) => item && item.id).map(coerceRampCase)
}

/** `GET /api/auto/ramp-cases/{ramp_id}`, one ramp case in full. */
export async function fetchRampCase(caseRef: string): Promise<HRCase> {
  const result = await apiClient.get<HRCase>(
    `/api/auto/ramp-cases/${encodeURIComponent(caseRef)}`
  )
  return coerceRampCase(result)
}

// ── The open decision ──────────────────────────────────────────────────────

export type RampDecisionAction = 'approve' | 'override'

export interface RampDecisionRequest {
  decision_id: string
  action: RampDecisionAction
  reason?: string
  decided_by: string
}

export interface RampDecisionResponse {
  accepted?: boolean
  delivered_to_auto?: boolean
  note?: string
}

/**
 * The id of the decision the specialist is answering.
 *
 * THE CONTRACT DOES NOT MAP THIS ONTO `HRCase`. The decision block carries
 * `decision_id`, but the field mapping table stops at `workbenchNote`, so there
 * is no declared field to read it from. Three sources, in order of how much they
 * are worth trusting:
 *
 *   1. a `decisionId` / `decision_id` the backend attaches to the case anyway,
 *   2. a nested `decision.decision_id`,
 *   3. the documented id shape, `EXC-{ramp_id}-{rule_id}-01`, rebuilt from the
 *      open policy trigger.
 *
 * `GET /pending-decision` is deliberately NOT used: the contract says it returns
 * the decision once and null thereafter, so reading it from here would consume
 * the hand-off the Auto side is polling for.
 */
export function rampDecisionId(c: HRCase): string {
  const loose = c as HRCase & {
    decisionId?: unknown
    decision_id?: unknown
    decision?: { decision_id?: unknown }
  }

  const direct = loose.decisionId ?? loose.decision_id ?? loose.decision?.decision_id
  if (typeof direct === 'string' && direct.trim()) return direct.trim()

  const open =
    c.policyTriggers.find((pt) => pt.outcome === 'escalate') ??
    c.policyTriggers.find((pt) => pt.outcome === 'approval_required')
  if (open?.policyId) return `EXC-${c.caseRef}-${open.policyId}-01`

  return `EXC-${c.caseRef}-01`
}

/** `POST /api/auto/ramp-cases/{ramp_id}/decision`. */
export async function postRampDecision(
  caseRef: string,
  body: RampDecisionRequest
): Promise<RampDecisionResponse> {
  return apiClient.post<RampDecisionResponse>(
    `/api/auto/ramp-cases/${encodeURIComponent(caseRef)}/decision`,
    body
  )
}

// ── The email thread ───────────────────────────────────────────────────────

export interface RampReplyResponse {
  sent?: boolean
  note?: string
}

/** `POST /api/auto/ramp-cases/{ramp_id}/reply`. */
export async function postRampReply(
  caseRef: string,
  message: string,
  sentBy: string = RAMP_OPERATOR
): Promise<RampReplyResponse> {
  return apiClient.post<RampReplyResponse>(
    `/api/auto/ramp-cases/${encodeURIComponent(caseRef)}/reply`,
    { message, sent_by: sentBy }
  )
}

// ── The hook ───────────────────────────────────────────────────────────────

/** `GET /api/auto/ramp-cases`. The store uses `fetchRampCases` directly. */
export function useRampCases(limit = RAMP_CASES_PAGE_SIZE) {
  const [data, setData] = useState<HRCase[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refetch = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await fetchRampCases(limit))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load ramp cases')
    } finally {
      setLoading(false)
    }
  }, [limit])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { data, loading, error, refetch }
}

// ── Auto's own audit trail ─────────────────────────────────────────────────

/**
 * The open decision exactly as Auto reported it.
 *
 * The backend attaches this to a ramp case as `rampDecision`. It is what the
 * Auto human-input form is built from, so rendering it is how the review panel
 * in this page and the form in the Auto app end up saying the same thing.
 */
export interface RampOpenDecision {
  decision_id?: string
  title?: string
  rule_id?: string
  rule_statement?: string
  contractual_basis?: { source?: string; reference?: string; text?: string }
  recommendation?: string
  rationale?: string
  affected?: Array<Record<string, unknown>>
  resequences?: string[]
  form_url?: string
  options?: Array<{ id?: string; label?: string; recommended?: boolean }>
  /** The last answer that could not be handed to Auto. The decision stays open. */
  delivery_failed?: { action?: string; reason?: string; attempted_at?: string }
}

/** The open decision on a ramp case, or null for a mock or a closed ramp. */
export function rampOpenDecision(c: HRCase): RampOpenDecision | null {
  const loose = c as HRCase & { rampDecision?: RampOpenDecision | null }
  const decision = loose.rampDecision
  return decision && typeof decision === 'object' ? decision : null
}

/**
 * One step of an Auto run, as the Auto app draws it.
 *
 * `outputHtml` is the rendered panel, already stripped server side. `child` is a
 * subworkflow's whole run, which is how an AI Employee's own steps appear under
 * the orchestrator step that called it.
 */
/** A file an AI coworker produced during the run. */
export interface AutoTrailFile {
  name: string
  url: string
}

export interface AutoTrailStep {
  id: string
  name: string
  description?: string
  status?: string
  attempt?: number | null
  started_at?: string
  completed_at?: string
  output_html?: string
  output_text?: string
  error?: string
  /** Files this step produced — a rendered PDF, an uploaded artifact. */
  files?: AutoTrailFile[]
  child?: AutoTrailRun | null
  /**
   * What the step read, which rules it applied, how many records it touched.
   *
   * Auto's trail endpoint does not return these today — the ramp event body
   * carries them and `app/services/auto_ramp.py` files them under the step's
   * `result`, which the case mapper does not pass on. Declared optional so the
   * audit trail reads them directly the day either side starts sending them, and
   * falls back to a JSON output until then.
   */
  rules_applied?: string[]
  evidence_read?: string[]
  records_examined?: number
  cleared_autonomously?: number
}

export interface AutoTrailRun {
  run_id: string
  workflow_name?: string
  workflow_description?: string
  status?: string
  started_at?: string
  completed_at?: string
  url?: string
  steps?: AutoTrailStep[]
}

/**
 * Every file the run produced, in the order the steps produced them.
 *
 * The Documents tab and the audit trail both need this, and neither should walk
 * the nested run shape itself. `producedBy` is the coworker whose step wrote the
 * file, which is the thing a reader asks first when a PDF appears on a case.
 */
export interface TrailFile extends AutoTrailFile {
  producedBy: string
  at?: string
}

export function trailFiles(trail: AutoTrailRun | null): TrailFile[] {
  if (!trail) return []
  const out: TrailFile[] = []
  const seen = new Set<string>()

  const walk = (run: AutoTrailRun, depth: number) => {
    for (const step of run.steps ?? []) {
      for (const file of step.files ?? []) {
        if (!file?.url || seen.has(file.url)) continue
        seen.add(file.url)
        out.push({
          ...file,
          producedBy: run.workflow_name || step.name || 'AI coworker',
          at: step.completed_at || step.started_at,
        })
      }
      if (step.child && depth < 3) walk(step.child, depth + 1)
    }
  }
  walk(trail, 0)
  return out
}

/**
 * Mail the run actually sent, recovered from the trail.
 *
 * The orchestrator pushes the INBOUND message as an email event and reports the
 * outbound closing reply only inside the step that sent it, so the Chat tab
 * showed a thread that stopped at the message that started it — on a case whose
 * whole point is that it replied. This reads the send back out of the step's own
 * output: a step that recorded a recipient and `sent` is a message that left.
 *
 * Deliberately narrow. Nothing here is inferred from prose, so a run that sent
 * nothing produces nothing.
 */
export interface TrailEmail {
  id: string
  senderName: string
  message: string
  timestamp: string
  attachment?: string
}

/**
 * The launch plan, read back out of the step that built it.
 *
 * A seeded case carries a whole plan block on the case itself. A LIVE one does
 * not: `app/services/auto_ramp.py` can only recover the cohorts, because the
 * cohorts are the only part of the plan the decision gate happens to restate.
 * Everything else — the milestones and their float, the device position behind
 * the shortfall — is sitting in the planner's own step output as JSON, which
 * the audit trail already fetches for this case.
 *
 * So the plan visual reads it from there rather than being handed a template.
 * These are Auto's figures, not ours; a run that stops reporting one of these
 * blocks drops that block from the screen instead of showing an invented one.
 *
 * Cost is deliberately absent. The planner does not report money, and a cost
 * card is not worth making one up for.
 */
function looksLikeThePlan(parsed: Record<string, unknown>): boolean {
  return Array.isArray(parsed.cohorts) && Array.isArray(parsed.backward_plan)
}

function asRecords(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value)
    ? value.filter((e): e is Record<string, unknown> => !!e && typeof e === 'object')
    : []
}

function num(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

/** `role_mix` is a label-to-count object, and an empty one is an absent block. */
function numberMap(value: unknown): Record<string, number> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined
  const out: Record<string, number> = {}
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    if (typeof raw === 'number' && Number.isFinite(raw)) out[key] = raw
  }
  return Object.keys(out).length > 0 ? out : undefined
}

/**
 * The device row the shortfall is actually about.
 *
 * The planner reports every model it holds at the site, and all but one of them
 * are fine. The row worth a card is the one the first cohort needs, and failing
 * that the one that is short; anything else is a stock report, not a problem.
 */
function bindingDevice(rows: Record<string, unknown>[]): Record<string, unknown> | null {
  const needed = rows.filter((r) => (num(r.cohort_1_requirement) ?? 0) > 0)
  const short = rows.filter((r) => (num(r.shortfall) ?? 0) > 0)
  return needed[0] ?? short[0] ?? null
}

export function trailPlan(trail: AutoTrailRun | null): LaunchPlan | null {
  if (!trail) return null
  let found: LaunchPlan | null = null

  const walk = (run: AutoTrailRun, depth: number) => {
    for (const step of run.steps ?? []) {
      if (found) return
      const raw = step.output_text?.trim()
      if (raw && raw.startsWith('{')) {
        try {
          const parsed = JSON.parse(raw) as Record<string, unknown>
          if (looksLikeThePlan(parsed)) {
            const device = bindingDevice(asRecords(parsed.procurement))
            found = {
              site: str(parsed.site),
              headcount: num(parsed.headcount_committed) ?? num(parsed.headcount_placed),
              go_live: str(parsed.go_live_date),
              cohorts: asRecords(parsed.cohorts).map((row) => ({
                n: num(row.cohort),
                people: num(row.headcount),
                starts: str(row.start_date),
                training_ends: str(row.training_ends),
                // The timeline draws its ladder from these. The planner emits
                // the first today; the other two are the planned extension to
                // the same step and are read the day they appear, with no
                // frontend change. Absent means that activity draws one
                // launch-wide bar, never a derived date.
                offers_accepted: str(row.offers_accepted_by),
                requisitions_open: str(row.requisitions_open),
                interviews_by: str(row.interviews_by),
              })),
              milestones: asRecords(parsed.backward_plan).map((row) => ({
                label: str(row.milestone),
                latest_start: str(row.date),
                float_days: num(row.float_days),
                status: str(row.status),
              })),
              // The working, for the collapsed block under the plan. All of it
              // is already in this same step; none of it is a second call.
              role_mix: numberMap(parsed.role_mix),
              procurement: asRecords(parsed.procurement).map((row) => ({
                model: str(row.device_model),
                on_hand: num(row.on_hand),
                inbound: num(row.inbound_qty),
                available: num(row.available),
                needed: num(row.cohort_1_requirement),
                short: num(row.shortfall),
                lead_time_days: num(row.lead_time_days),
                reorder_placed: str(row.purchase_order_by),
                lands: str(row.reorder_lands),
                float_days: num(row.float_days),
              })),
              binding_constraint:
                parsed.binding_constraint && typeof parsed.binding_constraint === 'object'
                  ? (() => {
                      const b = parsed.binding_constraint as Record<string, unknown>
                      return {
                        name: str(b.name),
                        float_days: num(b.float_days),
                        detail: str(b.detail),
                        assumed_lead_time_days: num(b.sow_assumed_lead_time_days),
                        misses_go_live_by_days: num(b.misses_go_live_by_days),
                        would_have_landed: str(b.would_have_landed_if_assumption_held),
                      }
                    })()
                  : undefined,
              rules_applied: Array.isArray(parsed.rules_applied)
                ? parsed.rules_applied.filter((r): r is string => typeof r === 'string')
                : undefined,
              verdict: str(parsed.verdict),
              intervention: str(parsed.intervention),
              devices: device
                ? {
                    model: str(device.device_model),
                    needed: num(device.cohort_1_requirement),
                    available: num(device.available),
                    short: num(device.shortfall),
                    on_hand: num(device.on_hand),
                    inbound: num(device.inbound_qty),
                    lead_time_days: num(device.lead_time_days),
                    reorder_placed: str(device.purchase_order_by),
                    lands: str(device.reorder_lands),
                    new_requests: num(device.new_requests),
                  }
                : undefined,
            }
            return
          }
        } catch {
          // Not JSON. Most steps are not, and that costs nothing.
        }
      }
      if (step.child && depth < 3) walk(step.child, depth + 1)
    }
  }
  walk(trail, 0)
  return found
}


/**
 * The exception that is waiting on a person, read back out of the step that raised it.
 *
 * The same story as `trailPlan`. The decision the Command Center is pushed carries
 * the rule, the recommendation and the rationale, but not the people: the fourteen
 * names, their managers, their start dates and the shape of each failure live in the
 * output of the AI coworker's own step. The audit trail already fetches that step for
 * this case, so the screen reads it from there rather than being handed a template.
 *
 * Only an exception that `requires_human` is returned. A coworker resolves most of
 * what it finds by itself, and a resolved finding is a line in the audit log, not a
 * question. Returning one here would put a decision on screen that nobody is being
 * asked to make.
 */
function asRecordList(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value)
    ? value.filter((e): e is Record<string, unknown> => !!e && typeof e === 'object')
    : []
}

function splitOf(value: unknown): Record<string, number> | undefined {
  if (!value || typeof value !== 'object') return undefined
  const out: Record<string, number> = {}
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    if (typeof raw === 'number' && Number.isFinite(raw)) out[key] = raw
  }
  return Object.keys(out).length > 0 ? out : undefined
}

/** "Move the 14 affected starters from cohort 2 to cohort 3." → 3 */
const MOVES_TO_RE = /to\s+cohort\s+(\d+)/i

export function trailException(trail: AutoTrailRun | null): CaseException | null {
  if (!trail) return null
  let found: CaseException | null = null

  const walk = (run: AutoTrailRun, depth: number) => {
    for (const step of run.steps ?? []) {
      if (found) return
      const raw = step.output_text?.trim()
      if (raw && raw.startsWith('{')) {
        try {
          const parsed = JSON.parse(raw) as Record<string, unknown>
          for (const entry of asRecordList(parsed.exceptions)) {
            if (entry.requires_human !== true) continue
            if (entry.auto_resolved === true) continue
            const rec =
              entry.recommendation && typeof entry.recommendation === 'object'
                ? (entry.recommendation as Record<string, unknown>)
                : {}
            const summary = str(rec.summary)
            const movesTo = summary ? MOVES_TO_RE.exec(summary)?.[1] : undefined
            found = {
              id: str(entry.exception_id),
              rule_id: str(entry.rule_id),
              rule_name: str(entry.rule_name),
              rule_statement: str(entry.rule_statement),
              raised_by: str(entry.raised_by) || run.workflow_name,
              severity: str(entry.severity),
              count: num(entry.affected_count),
              cohort: num(entry.affected_cohort),
              moves_to: movesTo ? Number(movesTo) : undefined,
              failure_split: splitOf(entry.failure_split),
              people: asRecordList(entry.affected_records).map((row) => ({
                id: str(row.candidate_id),
                name: str(row.full_name),
                role: str(row.role),
                manager: str(row.hiring_manager_id),
                start_date: str(row.start_date),
                failure: str(row.failure_shape),
                detail: str(row.detail),
              })),
              contractual_basis:
                entry.contractual_basis && typeof entry.contractual_basis === 'object'
                  ? {
                      source: str((entry.contractual_basis as Record<string, unknown>).source),
                      reference: str(
                        (entry.contractual_basis as Record<string, unknown>).reference
                      ),
                      text: str((entry.contractual_basis as Record<string, unknown>).text),
                    }
                  : undefined,
              recommendation: {
                action: str(rec.action),
                summary,
                rationale: str(rec.rationale),
              },
              resequences: Array.isArray(entry.resequences)
                ? entry.resequences.filter((r): r is string => typeof r === 'string')
                : undefined,
            }
            return
          }
        } catch {
          // Not JSON. Most steps are not, and that costs nothing.
        }
      }
      if (step.child && depth < 3) walk(step.child, depth + 1)
    }
  }
  walk(trail, 0)
  return found
}

export function trailEmails(trail: AutoTrailRun | null): TrailEmail[] {
  if (!trail) return []
  const out: TrailEmail[] = []

  const walk = (run: AutoTrailRun, depth: number) => {
    for (const step of run.steps ?? []) {
      const raw = step.output_text?.trim()
      if (raw && raw.startsWith('{')) {
        try {
          const parsed = JSON.parse(raw) as Record<string, unknown>
          const recipient = typeof parsed.recipient === 'string' ? parsed.recipient : ''
          if (parsed.sent === true && recipient) {
            const reason = typeof parsed.reason === 'string' ? parsed.reason : ''
            out.push({
              id: `trail-mail-${step.id || out.length}`,
              senderName: run.workflow_name || step.name || 'AI coworker',
              message: reason || `Replied on the original thread to ${recipient}.`,
              timestamp: step.completed_at || step.started_at || '',
              attachment: (step.files ?? [])[0]?.name,
            })
          }
        } catch {
          // Not JSON. Most steps are not, and that costs nothing.
        }
      }
      if (step.child && depth < 3) walk(step.child, depth + 1)
    }
  }
  walk(trail, 0)
  return out
}

/**
 * The files that came IN with the commitment, recovered from the intake step.
 *
 * The orchestrator records the attachment it pulled off the message inside the
 * step that read the mailbox, not on the case, so a live case showed only what
 * the run produced and none of what it was given. These have no download link:
 * the file is in OneDrive, which the Command Center holds no credential for, so
 * `location` is shown instead of pretending the row is clickable.
 */
export interface InboundAttachment {
  name: string
  location?: string
}

export function trailInboundAttachments(trail: AutoTrailRun | null): InboundAttachment[] {
  if (!trail) return []
  const out: InboundAttachment[] = []
  const seen = new Set<string>()

  const walk = (run: AutoTrailRun, depth: number) => {
    for (const step of run.steps ?? []) {
      const raw = step.output_text?.trim()
      if (raw && raw.startsWith('{')) {
        try {
          const parsed = JSON.parse(raw) as Record<string, unknown>
          const intake = parsed.intake
          const list =
            intake && typeof intake === 'object'
              ? (intake as Record<string, unknown>).attachments
              : undefined
          if (Array.isArray(list)) {
            for (const entry of list) {
              if (!entry || typeof entry !== 'object') continue
              const record = entry as Record<string, unknown>
              const name = typeof record.name === 'string' ? record.name.trim() : ''
              if (!name || seen.has(name.toLowerCase())) continue
              seen.add(name.toLowerCase())
              out.push({
                name,
                location:
                  typeof record.onedrive_path === 'string' ? record.onedrive_path : undefined,
              })
            }
          }
        } catch {
          // Not JSON. Most steps are not.
        }
      }
      if (step.child && depth < 3) walk(step.child, depth + 1)
    }
  }
  walk(trail, 0)
  return out
}



/**
 * `GET /api/auto/ramp-cases/{ramp_id}/auto-trail`.
 *
 * Answers null rather than failing when Auto cannot be read, so the caller does
 * not need a second error path: no trail simply means no panel.
 */
export async function fetchRampAutoTrail(caseRef: string): Promise<AutoTrailRun | null> {
  const result = await apiClient.get<AutoTrailRun | null>(
    `/api/auto/ramp-cases/${encodeURIComponent(caseRef)}/auto-trail`
  )
  return result && typeof result === 'object' && result.run_id ? result : null
}

/**
 * The last trail seen for each ramp, for the length of the page's life.
 *
 * Opening the Audit trail used to start from nothing every time: the hook
 * mounts, state is null, the panel draws its empty frame and the run appears a
 * second or two later when ~200KB has come back from Auto through the backend.
 * You paid that on every visit to a case you had already looked at. "If I go to
 * workbench, if I go to audit trail, it takes a bit of time." (21 Sep.)
 *
 * So the last good trail is kept here and handed to the next mount immediately,
 * while the fetch behind it revalidates. Deliberately module scope rather than
 * a store: it is a render cache, it is allowed to be thrown away by a reload,
 * and nothing should ever read it except the hook below.
 */
const trailCache = new Map<string, AutoTrailRun>()

/** Refs already being warmed, so a four second poll does not start it again. */
const prefetching = new Set<string>()

/**
 * Fetch a trail nobody has asked for yet, quietly.
 *
 * The first read of a trail costs about four seconds: a megabyte of JSON
 * assembled from a dozen calls to Auto. Every read after it costs about three
 * hundred milliseconds, because both ends now cache it. The whole of that first
 * cost used to land on the person who clicked, in the pause between the queue
 * appearing and them choosing a case, which is exactly the time it could have
 * been spent in.
 *
 * Fire and forget, one case at a time, and silent: a launch whose trail cannot
 * be read is a launch whose Audit trail falls back to the pushed steps, which
 * is what it did before anyone was warming anything.
 */
export function prefetchRampAutoTrail(caseRef: string | null | undefined): void {
  if (!caseRef || trailCache.has(caseRef) || prefetching.has(caseRef)) return
  prefetching.add(caseRef)
  fetchRampAutoTrail(caseRef)
    .then((trail) => {
      if (trail) trailCache.set(caseRef, trail)
    })
    .catch(() => {})
    .finally(() => prefetching.delete(caseRef))
}

/**
 * Auto's trail for one ramp, refreshed while the run is still moving.
 *
 * Polls on a five second heartbeat and stops once Auto says the run has
 * finished: a completed run is immutable, so re-reading 200KB of it every five
 * seconds for the rest of the demo buys nothing. The backend holds finished
 * runs for an hour for the same reason.
 */
export function useRampAutoTrail(caseRef: string | null | undefined) {
  // Seeded from the cache on the very first render, so a case you have already
  // opened draws its trail in the same frame as the rest of the screen.
  const [trail, setTrail] = useState<AutoTrailRun | null>(
    () => (caseRef ? trailCache.get(caseRef) ?? null : null)
  )
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!caseRef) {
      setTrail(null)
      return
    }
    let cancelled = false
    let timer: ReturnType<typeof setInterval> | null = null

    const load = () => {
      fetchRampAutoTrail(caseRef)
        .then((next) => {
          if (cancelled) return
          if (next) trailCache.set(caseRef, next)
          setTrail(next)
          setLoading(false)
          const done = next?.status === 'completed' || next?.status === 'failed'
          if (done && timer) {
            clearInterval(timer)
            timer = null
          }
        })
        .catch(() => {
          if (!cancelled) setLoading(false)
        })
    }

    // Switching cases: show what is known about THIS one straight away rather
    // than the previous case's trail or an empty frame.
    const known = trailCache.get(caseRef) ?? null
    setTrail(known)
    // Only a case with nothing cached is "loading". One that has been seen
    // before is on screen already and is merely being checked.
    setLoading(known === null)
    load()
    timer = setInterval(load, 5_000)

    return () => {
      cancelled = true
      if (timer) clearInterval(timer)
    }
  }, [caseRef])

  return { trail, loading }
}

/**
 * The exception a case carries on its own, with no Auto run behind it.
 *
 * `trailException` reads the fourteen blocked starters out of the step that
 * raised them, which is the right source on a live launch and the only source
 * the Cardiff case has. A seeded launch has no Auto run at all — and it does
 * carry the same people, on the open decision, as `affected`. Without this the
 * "See who is affected" card on Aberdeen, Belfast and Sheffield was a button
 * that opened nothing, which reads as a broken screen rather than as a missing
 * extra.
 *
 * Everything below is read off the decision. The count is the number of people
 * it actually names; the cohorts are the ones its own recommendation names; the
 * split is those people grouped by the reason each was given. Nothing is
 * derived that the decision does not say, so a decision without `affected`
 * produces no exception and the card stays un-clickable.
 */
const MOVES_FROM_RE = /from\s+cohort\s+(\d+)/i

export function caseException(c: HRCase): CaseException | null {
  const decision = rampOpenDecision(c)
  const affected = Array.isArray(decision?.affected) ? decision.affected : []
  if (!decision || affected.length === 0) return null

  const summary = decision.recommendation ?? ''
  const people = affected.map((row) => {
    const person = row as Record<string, unknown>
    return {
      id: str(person.candidate_id) ?? str(person.id),
      name: str(person.name) ?? str(person.full_name),
      role: str(person.role),
      manager: str(person.manager) ?? str(person.hiring_manager_id),
      start_date: str(person.start_date),
      failure: str(person.reason) ?? str(person.failure_shape),
      detail: str(person.detail),
    }
  })

  const split: Record<string, number> = {}
  for (const person of people) {
    if (!person.failure) continue
    split[person.failure] = (split[person.failure] ?? 0) + 1
  }

  return {
    id: decision.decision_id,
    rule_id: decision.rule_id,
    rule_statement: decision.rule_statement,
    count: people.length,
    cohort: numberFrom(MOVES_FROM_RE.exec(summary)?.[1]),
    moves_to: numberFrom(MOVES_TO_RE.exec(summary)?.[1]),
    failure_split: Object.keys(split).length > 0 ? split : undefined,
    people,
    contractual_basis: decision.contractual_basis,
    recommendation: { summary, rationale: decision.rationale },
    resequences: decision.resequences,
  }
}

function numberFrom(value: string | undefined): number | undefined {
  if (!value) return undefined
  const n = Number(value)
  return Number.isFinite(n) ? n : undefined
}

/**
 * Two readings of the same cohorts, joined on the cohort number.
 *
 * Whichever side holds a field wins, the case first, so nothing a seeded case
 * carries is lost and nothing the run reports is dropped. A cohort that exists
 * on only one side is kept as it stands.
 */
function mergeCohorts(
  base: PlanCohort[] | undefined,
  fromTrail: PlanCohort[] | undefined
): PlanCohort[] | undefined {
  if (!base || base.length === 0) return fromTrail
  if (!fromTrail || fromTrail.length === 0) return base

  return base.map((cohort, index) => {
    const match =
      fromTrail.find((other) => other.n !== undefined && other.n === cohort.n) ??
      fromTrail[index]
    if (!match) return cohort
    return {
      n: cohort.n ?? match.n,
      people: cohort.people ?? match.people,
      starts: cohort.starts || match.starts,
      training_ends: cohort.training_ends || match.training_ends,
      offers_accepted: cohort.offers_accepted || match.offers_accepted,
      requisitions_open: cohort.requisitions_open || match.requisitions_open,
      interviews_by: cohort.interviews_by || match.interviews_by,
    }
  })
}

/**
 * The plan a case should draw, whatever kind of case it is.
 *
 * A seeded case carries the whole block, including the money, and needs nothing
 * from Auto. A live one carries the cohorts and nothing else, so the milestones
 * and the device position are read back out of the planner's own step output by
 * `trailPlan`. Merging per block rather than per plan means a seeded case never
 * loses its cost card to a run that does not report one, and a live case never
 * shows an empty frame where the run genuinely said nothing.
 *
 * `enabled` exists so the trail is only fetched while the plan is actually on
 * screen. The case list already polls every four seconds; the plan does not
 * need to.
 */
export function useCasePlan(
  caseRef: string | null | undefined,
  base: LaunchPlan | null,
  enabled: boolean
): LaunchPlan | null {
  const { trail } = useRampAutoTrail(enabled ? caseRef : null)
  const fromTrail = trailPlan(trail)
  if (!base) return fromTrail
  if (!fromTrail) return base

  const filled = <T,>(value: T[] | undefined): boolean => !!value && value.length > 0
  return {
    site: base.site || fromTrail.site,
    headcount: base.headcount ?? fromTrail.headcount,
    go_live: base.go_live || fromTrail.go_live,
    // Merged field by field rather than whole-array. The case carries the
    // cohorts the queue was pushed — number, headcount, start date — and the
    // planner's own step carries the rest of each cohort's timetable. Taking
    // one array or the other dropped `offers_accepted_by`, which is the
    // difference between the timeline drawing a ladder and drawing one bar.
    cohorts: mergeCohorts(base.cohorts, fromTrail.cohorts),
    milestones: filled(base.milestones) ? base.milestones : fromTrail.milestones,
    devices: base.devices ?? fromTrail.devices,
    cost: base.cost ?? fromTrail.cost,
    // The working. Only the run carries it, so the case never overrides it.
    role_mix: base.role_mix ?? fromTrail.role_mix,
    procurement: filled(base.procurement) ? base.procurement : fromTrail.procurement,
    binding_constraint: base.binding_constraint ?? fromTrail.binding_constraint,
    rules_applied: filled(base.rules_applied) ? base.rules_applied : fromTrail.rules_applied,
    verdict: base.verdict || fromTrail.verdict,
    intervention: base.intervention || fromTrail.intervention,
  }
}

/**
 * The exception a case should draw, with the cohort dates it needs to draw the move.
 *
 * The coworker that raises the exception knows who is blocked and why, but it does
 * not restate the launch timetable, so on its own the move card could say "cohort 2
 * to cohort 3" without either date. The cohorts come from the plan, which is read
 * back from the planner's step by `trailPlan`, so both halves of this screen are
 * Auto's own figures and neither is invented.
 *
 * `enabled` keeps the trail fetch to the moments the dialog is actually open.
 */
export function useCaseException(
  caseRef: string | null | undefined,
  base: LaunchPlan | null,
  enabled: boolean,
  /** What the case carries itself, for a launch with no Auto run behind it. */
  fallback: CaseException | null = null
): CaseException | null {
  const { trail } = useRampAutoTrail(enabled ? caseRef : null)
  // The run's own step wins where there is one; the decision block is what a
  // seeded launch has instead, and it names the same people.
  const exception = trailException(trail) ?? fallback
  if (!exception) return null

  const cohorts = base?.cohorts ?? trailPlan(trail)?.cohorts ?? []
  const from = cohorts.find((c) => c.n === exception.cohort)
  const to = cohorts.find((c) => c.n === exception.moves_to)

  return {
    ...exception,
    from_start: from?.starts,
    to_start: to?.starts,
    go_live: base?.go_live ?? trailPlan(trail)?.go_live,
    cohort_from_size: from?.people,
    cohort_to_size: to?.people,
  }
}
